import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import SiteManager from "../types/siteManager";
import SearchResults from "../types/searchResults";
import SearchParams, { defaultParams } from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";

interface SiteManagerState {
  siteManager: SiteManager;
  siteManagerSearchResults?: SearchResults;
  siteManagerFilter: SearchParams;
  status: "idle" | "loading" | "failed";
}

const initialState: SiteManagerState = {
  siteManager: {
    id: 0,
    firstName: "",
    lastName: "",
    email: "",
  },
  siteManagerFilter: defaultParams,
  siteManagerSearchResults: undefined,
  status: "idle",
};

export const getSiteManagerId = createAsyncThunk<SiteManager, number>(
  "site-manager/get/id",
  async (id: number) => {
    const response = await http.get<SiteManager>(`/sitemanager/${id}`);
    return response.data;
  }
);

export const editSiteManager = createAsyncThunk<SiteManager, SiteManager>(
  "site-manager/edit",
  async (siteManager: SiteManager) => {
    const response = await http.put<SiteManager>(
      `/sitemanager/${siteManager.id}`,
      siteManager,
      {
        params: {
          successToast: true,
        },
      }
    );
    return response.data;
  }
);

export const deleteSiteManager = createAsyncThunk<SiteManager, number>(
  "site-manager/delete",
  async (id: number) => {
    const response = await http.delete<SiteManager>(`/sitemanager/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const postSiteManager = createAsyncThunk<SiteManager, SiteManager>(
  "site-manager/post",
  async (siteManager: SiteManager) => {
    const response = await http.post<SiteManager>(`/sitemanager`, siteManager, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const searchSiteManager = createAsyncThunk<SearchResults, SearchParams>(
  "site-manager/search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(`sitemanager/search?${qs}`);
    return response.data;
  }
);

const siteManagerSlice = createSlice({
  name: "siteManager",
  initialState,
  reducers: {
    resetSiteManager(state) {
      state.siteManager = { ...initialState.siteManager };
    },
    setSiteManagerFilter(state, action) {
      state.siteManagerFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSiteManagerId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSiteManagerId.fulfilled, (state, action) => {
        state.status = "idle";
        state.siteManager = action.payload;
      })
      .addCase(getSiteManagerId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editSiteManager.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editSiteManager.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(editSiteManager.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteSiteManager.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteSiteManager.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteSiteManager.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postSiteManager.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postSiteManager.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(postSiteManager.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchSiteManager.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchSiteManager.fulfilled, (state, action) => {
        state.status = "idle";
        state.siteManagerSearchResults = action.payload;
      })
      .addCase(searchSiteManager.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default siteManagerSlice.reducer;
export const { resetSiteManager, setSiteManagerFilter } =
  siteManagerSlice.actions;
